import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import BrandAdLoader from "web/react/components/brand-ad-loader/brand-ad-loader";
import FeedCountAndSort from "web/react/components/feed-count-and-sort/feed-count-and-sort";
import FeedGrid from "web/react/components/feed-grid/feed-grid";
import { FeedHeader } from "web/react/components/feed-header/feed-header";
import { FilterContainerEmo } from "web/react/components/filters-emo/filter-container-emo";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";
import { RelatedRail } from "web/react/components/related-rail/related-rail";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { SaveSearchButton } from "web/react/components/save-search-button/save-search-button";
import { setFeedBreadcrumbs } from "web/redux/ducks/feed-breadcrumbs";
import store, { ReduxStoreState } from "web/redux/store";
import environment from "web/script/modules/environment";
import { languageIsEnglish } from "web/script/modules/language";
import {
    AdsenseForShoppingSerializer,
    FeedHeaderSerializer,
    FeedNavigationSerializer,
    ProductFeedSerializer,
    RecentlyViewedSerializer,
    RelatedLinksSerializer,
    RelatedRailSerializer,
} from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";
import * as styles from "./feed.css";
import { FeedPage, getFeedSubTitle, getFeedTitle } from "./utils";

interface FeedProps {
    header: FeedHeaderSerializer;
    universalFilters: UniversalFiltersData;
    productFeed: ProductFeedSerializer;
    adsenseForShopping: AdsenseForShoppingSerializer;
    feedNavigation: FeedNavigationSerializer;
    relatedSearchesRail: RelatedLinksSerializer;
    relatedDesignersRail: RelatedRailSerializer;
    relatedRetailersRail: RelatedRailSerializer;
    relatedStoreBrandsRail: RelatedRailSerializer;
    recentlyViewedRail: RecentlyViewedSerializer;
}

function Feed({
    header,
    universalFilters,
    productFeed,
    adsenseForShopping,
    feedNavigation,
    relatedSearchesRail,
    relatedDesignersRail,
    relatedRetailersRail,
    relatedStoreBrandsRail,
    recentlyViewedRail,
}: FeedProps): React.ReactElement {
    const feedPageType = environment.get("feedType");

    useEffect(() => {
        store.dispatch(setFeedBreadcrumbs(feedNavigation?.shop_breadcrumbs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feedCountState = useSelector((state: ReduxStoreState) => state.feedCount);
    const productCount = feedCountState.product_count ?? productFeed.feed_count?.product_count ?? 0;
    const retailerCount =
        feedCountState.retailer_count ?? productFeed.feed_count?.retailer_count ?? 0;

    const feedHeaderState = useSelector((state: ReduxStoreState) => state.feedHeader);
    const feedHeader = feedHeaderState.gender ? feedHeaderState : header;

    const mainTitle = getFeedTitle(feedPageType, feedHeader, universalFilters);
    const subtitle = getFeedSubTitle(feedPageType);
    const actions =
        (feedPageType === FeedPage.SEARCH && languageIsEnglish() && (
            <SaveSearchButton
                term={feedHeader.title ?? ""}
                gender={feedHeader.gender}
                next={feedHeader.search_next ?? ""}
                variant="emo"
            />
        )) ||
        undefined;

    let shortDescription: string | null = null;
    let description: string | null = null;
    if (feedPageType !== FeedPage.RELATED_PRODUCTS) {
        shortDescription = feedHeader.short_description;
        description = feedHeader.description;
    }

    return (
        <>
            {/* Note: We're still using the `header` from the server and not redux */}
            {header?.brand_ad && (
                <div className={styles.brandAd}>
                    <BrandAdLoader {...header?.brand_ad} />
                </div>
            )}
            <div className={styles.feed}>
                <FeedHeader
                    title={mainTitle}
                    subtitle={subtitle}
                    profile={feedHeader}
                    actions={actions}
                    shortDescription={shortDescription}
                    description={description}
                    type={feedPageType}
                    productCount={productCount}
                    retailerCount={retailerCount}
                />
                <FilterContainerEmo
                    filters={universalFilters}
                    productCount={productCount}
                    sortOptions={productFeed.feed_sort_dropdown.sort_options}
                />
                <div className={styles.feedGrid}>
                    <FeedCountAndSort
                        productCount={productCount}
                        retailerCount={retailerCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                    />
                    <FeedGrid
                        data={productFeed}
                        shortDescription={shortDescription}
                        description={description}
                    />
                    {adsenseForShopping && <AdsenseShopping {...adsenseForShopping} />}
                    {recentlyViewedRail && (
                        <RecentlyViewed
                            feedType={productFeed.type || ""}
                            title={recentlyViewedRail.title}
                            current_product_id={recentlyViewedRail.current_product_id}
                            in_overlay={recentlyViewedRail.in_overlay}
                            products={recentlyViewedRail.products}
                            is_loading={recentlyViewedRail.is_loading}
                        />
                    )}
                    {relatedSearchesRail?.links.length > 0 && (
                        <RelatedSearchesRail
                            relatedSearches={relatedSearchesRail.links}
                            title={relatedSearchesRail.title}
                        />
                    )}
                    {relatedDesignersRail?.candidates && (
                        <RelatedRail
                            title={relatedDesignersRail.title}
                            candidates={relatedDesignersRail.candidates}
                            analyticsLabel={"designers"}
                        />
                    )}
                    {relatedStoreBrandsRail?.candidates && (
                        <RelatedRail
                            title={relatedStoreBrandsRail.title}
                            candidates={relatedStoreBrandsRail.candidates}
                            analyticsLabel={"designers"}
                        />
                    )}
                    {relatedRetailersRail?.candidates && (
                        <RelatedRail
                            title={relatedRetailersRail.title}
                            candidates={relatedRetailersRail.candidates}
                            analyticsLabel={"retailers"}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

export default Feed;
